import { apiRequest, apiRequestBinary, type ApiBinaryResponse } from "@/lib/api";

// ─── Types ──────────────────────────────────────────────────────────────────

export type CertRequestStatus =
  | "draft"
  | "submitted"
  | "verified"
  | "issued"
  | "rejected"
  | "revoked";

export type CertRequestDocType =
  | "rut"
  | "camara_comercio"
  | "cedula"
  | "poder"
  | "formulario";

export type CertRequestAuditAction =
  | "approve"
  | "reject"
  | "sync"
  | "finalize"
  | "mark_ready"
  | "run_testsetid"
  | "sync_tenant";

export interface CertRequestCompanySummary {
  id: string;
  nit: string;
  dv: string | null;
  legalName: string;
  teamId: string;
  onboardingStatus: string;
  dianEnv: string;
  certUploaded: boolean;
}

/**
 * Mirrors the raw `cert_requests` row that the API returns under `certRequest`.
 * The API does not transform field names for this object — we keep the snake_case
 * shape to match what comes off D1.
 */
export interface CertRequestRow {
  id: string;
  company_id: string;
  team_id: string;
  pahcert_subscriber_id: string | null;
  pahcert_request_id: string | null;
  pahcert_certificate_serial: string | null;
  cert_type: string;
  cert_class: string;
  validity_years: number;
  status: CertRequestStatus;
  rejection_reason: string | null;
  revocation_reason: string | null;
  created_by_user_id: string;
  created_at: string;
  submitted_at: string | null;
  verified_at: string | null;
  issued_at: string | null;
  rejected_at: string | null;
  revoked_at: string | null;
  last_synced_at: string | null;
}

export interface CertRequestDocument {
  id: string;
  docType: CertRequestDocType | string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  pahcertArtifactId: string | null;
  uploadedAt: string;
}

export interface CertRequestAuditEntry {
  id: string;
  action: CertRequestAuditAction | string;
  fromStatus: string | null;
  toStatus: string | null;
  detail: unknown;
  actor: string;
  createdAt: string;
}

export interface CertRequestDetail {
  company: CertRequestCompanySummary;
  certRequest: CertRequestRow | null;
  documents: CertRequestDocument[];
  audit: CertRequestAuditEntry[];
}

/** Empty-shell response when the company has no cert request yet. */
export type CertRequestDetailOrEmpty =
  | CertRequestDetail
  | { certRequest: null; documents: []; audit: [] };

// ── Mutation request bodies ────────────────────────────────────────────────

export interface RejectCertRequestBody {
  reason: string;
}

export interface SoftwareProfile {
  softwareId: string;
  softwarePin: string;
  testSetId?: string;
}

export interface FinalizeCertRequestBody {
  softwareInvoice: SoftwareProfile;
  softwarePos: SoftwareProfile;
  softwarePayroll: SoftwareProfile;
  alias?: string;
}

export interface RunTestsetIdBody {
  softwareId: string;
  softwarePin: string;
  testSetId: string;
  resolutionNumber: string;
  resolutionDate: string;
  prefix: string;
  fromNumber: number;
  toNumber: number;
  validDateFrom: string;
  validDateTo: string;
  technicalKey: string;
}

// ── Mutation response shapes ───────────────────────────────────────────────

export interface SyncCertRequestResponse {
  pahcert: {
    status: string;
    certificateSerial?: string | null;
    [k: string]: unknown;
  };
}

export interface ApproveCertRequestResponse {
  ok: true;
  status: "verified";
}

export interface RejectCertRequestResponse {
  ok: true;
  status: "rejected";
  reason: string;
}

export interface FinalizeCertRequestResponse {
  ok: true;
  status: "issued";
  certUploaded: true;
}

export interface RunTestsetIdResponse {
  ok: true;
  result: { verdict?: string; [k: string]: unknown };
}

export interface SyncTenantResponse {
  ok: true;
  result: unknown;
}

export interface MarkReadyResponse {
  ok: true;
  status: "ready";
}

// ─── Endpoint base ──────────────────────────────────────────────────────────

function base(companyId: string): string {
  return `/api/admin/companies/${encodeURIComponent(companyId)}`;
}

// ─── Queries / mutations ────────────────────────────────────────────────────

export function getCertRequest(companyId: string): Promise<CertRequestDetailOrEmpty> {
  return apiRequest(`${base(companyId)}/cert-request`);
}

export function downloadCertRequestDocument(
  companyId: string,
  certRequestId: string,
  documentId: string,
): Promise<ApiBinaryResponse> {
  return apiRequestBinary(
    `${base(companyId)}/cert-requests/${encodeURIComponent(certRequestId)}` +
      `/documents/${encodeURIComponent(documentId)}/content`,
  );
}

export function syncCertRequest(companyId: string): Promise<SyncCertRequestResponse> {
  return apiRequest(`${base(companyId)}/cert-request/sync`, { method: "POST" });
}

export function approveCertRequest(companyId: string): Promise<ApproveCertRequestResponse> {
  return apiRequest(`${base(companyId)}/cert-request/approve`, { method: "POST" });
}

export function rejectCertRequest(
  companyId: string,
  reason: string,
): Promise<RejectCertRequestResponse> {
  return apiRequest(`${base(companyId)}/cert-request/reject`, {
    method: "POST",
    body: { reason } satisfies RejectCertRequestBody,
  });
}

export function finalizeCertRequest(
  companyId: string,
  body: FinalizeCertRequestBody,
): Promise<FinalizeCertRequestResponse> {
  return apiRequest(`${base(companyId)}/cert-request/finalize`, { method: "POST", body });
}

export function runTestsetid(
  companyId: string,
  body: RunTestsetIdBody,
): Promise<RunTestsetIdResponse> {
  return apiRequest(`${base(companyId)}/run-testsetid`, { method: "POST", body });
}

export function syncTenant(companyId: string): Promise<SyncTenantResponse> {
  return apiRequest(`${base(companyId)}/sync-tenant`, { method: "POST" });
}

export function markReady(companyId: string): Promise<MarkReadyResponse> {
  return apiRequest(`${base(companyId)}/mark-ready`, { method: "POST" });
}
